import { useBoolean } from '@fluentui/react-hooks';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { GridComponent } from '@syncfusion/ej2-react-grids';
import { FC, RefObject, useRef } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router';
import { applicationInsightsReactPlugin } from '../../../ApplicationInsightsService';
import { DrillDownContextProvider } from '../../../contexts/drillDownContext';
import FormViewProvider from '../../../contexts/formViewContext';
import { GridScrollContextProvider } from '../../../contexts/gridScrollContext';
import { GridStateContextProvider } from '../../../contexts/gridStateContext';
import { searchOnType, SearchTableContextProvider } from '../../../contexts/searchTableContext';
import TaxReturnEventHandlerContext from '../../../contexts/taxReturnEventHandlerContext';
import { useUserSettings } from '../../../contexts/useUserSettings';
import { VarianceNavigationContextProvider } from '../../../contexts/varianceNavigationContext';
import { COMPARE_REPORT_URL, REPORTS_TABLE_URL, VIEW_REPORT_URL } from '../../../utils/Urls.constant';
import ReturnHasBeenDeletedNotificationModal from '../../common/ReturnHasBeenDeletedNotificationModal';
import CompareReportsTable from './CompareReportsTable';

const CompareReportLines: FC = () => {
    const { reportId, versionId } = useParams();
    const reportIdAsNumber: number = reportId ? parseInt(reportId) : 0;
    const gridRef: RefObject<GridComponent> | null = useRef<GridComponent>(null);
    const navigate: NavigateFunction = useNavigate();
    const navigateToCompareReport = (versionId: string, selectedEntities: string[]) => {
        navigate(`${COMPARE_REPORT_URL}${reportIdAsNumber}/${versionId}?selectedEntities=${selectedEntities?.join(',')}`);
    };
    const navigateToIndividualReport = () => navigate(`${VIEW_REPORT_URL}${reportIdAsNumber}`);
    const [isReturnHasBeenDeletedModalOpen, { setTrue: openReturnHasBeenDeletedModal, setFalse: closeReturnHasBeenDeletedModalOpen }] =
        useBoolean(false);
    const { removeReturn } = useUserSettings();

    const onConfirmDeletionModal = () => {
        closeReturnHasBeenDeletedModalOpen();
        removeReturn(reportIdAsNumber);
        navigate(REPORTS_TABLE_URL);
    };

    return (
        <DrillDownContextProvider>
            <SearchTableContextProvider searchOn={searchOnType.Report}>
                <GridScrollContextProvider>
                    <GridStateContextProvider grid={gridRef}>
                        <FormViewProvider returnKey={reportIdAsNumber}>
                            <VarianceNavigationContextProvider>
                                <TaxReturnEventHandlerContext
                                    reportId={reportIdAsNumber}
                                    deleteEventCallback={openReturnHasBeenDeletedModal}
                                >
                                    <CompareReportsTable
                                        key={`${reportId} - ${versionId ?? 'NO_VERSION'}`}
                                        reportId={reportIdAsNumber}
                                        versionId={versionId!}
                                        errorModalOnClose={navigateToIndividualReport}
                                        navigateToCompareReport={navigateToCompareReport}
                                        navigateToReport={navigateToIndividualReport}
                                    />
                                    <ReturnHasBeenDeletedNotificationModal
                                        isModalVisible={isReturnHasBeenDeletedModalOpen}
                                        onConfirm={onConfirmDeletionModal}
                                    />
                                </TaxReturnEventHandlerContext>
                            </VarianceNavigationContextProvider>
                        </FormViewProvider>
                    </GridStateContextProvider>
                </GridScrollContextProvider>
            </SearchTableContextProvider>
        </DrillDownContextProvider>
    );
};

export default withAITracking(applicationInsightsReactPlugin, CompareReportLines, 'Compare return');
